import React, { useCallback, useEffect, useMemo, useRef } from "react";
import {
  ReactFlow,
  ReactFlowProvider,
  useNodesState,
  useEdgesState,
  addEdge,
  applyNodeChanges,
  applyEdgeChanges,
  Background,
  BackgroundVariant,
  Controls,
  Panel,
  Position,
  type Node,
  type Edge,
  type Connection,
  type OnConnect,
  type OnNodesChange,
  type OnEdgesChange,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { toast } from "react-toastify";
import SegmentNode from "./SegmentNode";
import CustomNotificationNode from "./CustomNotificationNode";
import TemplateNode from "./TemplateNode";

const nodeTypes = {
  segment: SegmentNode,
  customNotification: CustomNotificationNode,
  template: TemplateNode,
};

const NODE_TOP_Y = 20;
const SEGMENT_X = 50;
const NOTIFICATION_X = 380;

const initialNodes: Node[] = [
  {
    id: "segment-1",
    type: "segment",
    position: { x: SEGMENT_X, y: NODE_TOP_Y },
    data: { segmentId: "", segmentName: "" },
    sourcePosition: Position.Right,
  },
];

const initialEdges: Edge[] = [];

export interface FlowBuilderButton {
  id: string;
  text: string;
  url: string;
}

export interface FlowBuilderData {
  segmentId: string;
  segmentName?: string;
  /** Segment card details for edit mode (from row.segment or fetched) */
  segmentDescription?: string;
  segmentTotalUsersCount?: number;
  segmentPercentage?: number;
  segmentUpdateCycle?: string;
  segmentLastUpdatedAt?: string;
  segmentIsActive?: boolean;
  segmentIsLiveSegmentation?: boolean;
  notificationType: "custom" | "template";
  title?: string;
  body?: string;
  image?: string;
  link?: string;
  buttons?: FlowBuilderButton[];
  templateId?: string;
}

interface FlowBuilderCanvasProps {
  segments: { _id: string; name: string }[];
  templates: { value: string; label: string }[];
  onFlowDataChange?: (data: FlowBuilderData | null) => void;
  initialFlowData?: FlowBuilderData | null;
}

function FlowBuilderCanvasInner({
  segments,
  templates,
  onFlowDataChange,
  initialFlowData,
}: FlowBuilderCanvasProps) {
  const computedInitialNodes = useMemo((): Node[] => {
    if (!initialFlowData?.segmentId) return initialNodes;
    const segmentName =
      initialFlowData.segmentName ??
      segments.find((s) => s._id === initialFlowData.segmentId)?.name ??
      "";
    const segmentNode: Node = {
      id: "segment-1",
      type: "segment",
      position: { x: SEGMENT_X, y: NODE_TOP_Y },
      data: {
        segmentId: initialFlowData.segmentId,
        segmentName,
        description: initialFlowData.segmentDescription,
        totalUsersCount: initialFlowData.segmentTotalUsersCount,
        percentage: initialFlowData.segmentPercentage,
        updateCycle: initialFlowData.segmentUpdateCycle,
        lastUpdatedAt: initialFlowData.segmentLastUpdatedAt,
        isActive: initialFlowData.segmentIsActive,
        isLiveSegmentation: initialFlowData.segmentIsLiveSegmentation,
        segments,
        templates,
      },
      sourcePosition: Position.Right,
    };
    if (initialFlowData.notificationType === "custom") {
      return [
        segmentNode,
        {
          id: "custom-notification-1",
          type: "customNotification",
          position: { x: NOTIFICATION_X, y: NODE_TOP_Y },
          data: {
            title: initialFlowData.title ?? "",
            body: initialFlowData.body ?? "",
            image: initialFlowData.image,
            link: initialFlowData.link,
            buttons: initialFlowData.buttons,
          },
          targetPosition: Position.Left,
        },
      ];
    }
    const templateLabel =
      templates.find((t) => t.value === initialFlowData.templateId)?.label ?? "";
    return [
      segmentNode,
      {
        id: "template-1",
        type: "template",
        position: { x: NOTIFICATION_X, y: NODE_TOP_Y },
        data: {
          templateId: initialFlowData.templateId ?? "",
          templateName: templateLabel,
          templates,
        },
        targetPosition: Position.Left,
      },
    ];
  }, [initialFlowData, segments, templates]);

  const computedInitialEdges = useMemo((): Edge[] => {
    if (!initialFlowData?.segmentId) return initialEdges;
    const targetId =
      initialFlowData.notificationType === "custom"
        ? "custom-notification-1"
        : "template-1";
    return [{ id: "e-segment-1", source: "segment-1", target: targetId }];
  }, [initialFlowData]);

  const [nodes, setNodes] = useNodesState(computedInitialNodes);
  const [edges, setEdges] = useEdgesState(computedInitialEdges);
  const customCountRef = useRef(1);
  const templateCountRef = useRef(1);
  const reactFlowInstanceRef = useRef<any>(null);

  const onInit = useCallback((instance: any) => {
    reactFlowInstanceRef.current = instance;
  }, []);

  const handleAddCustomNotification = useCallback(
    (e?: React.MouseEvent) => {
      e?.stopPropagation?.();
      const hasTemplate = nodes.some((n) => n.type === "template");
      if (hasTemplate) {
        toast.error(
          "Already added Template. Remove the Template node and then add Custom Notification.",
        );
        return;
      }
      customCountRef.current += 1;
      const id = `custom-notification-${customCountRef.current}`;
      setNodes((nds) => {
        const withoutCustom = nds.filter(
          (n) => n.type !== "customNotification",
        );
        const newNode: Node = {
          id,
          type: "customNotification",
          position: { x: NOTIFICATION_X, y: NODE_TOP_Y },
          data: { title: "", body: "" },
          targetPosition: Position.Left,
        };
        return [...withoutCustom, newNode];
      });
      setTimeout(
        () => reactFlowInstanceRef.current?.fitView?.({ padding: 0.2 }),
        100,
      );
    },
    [setNodes, nodes],
  );

  const handleAddTemplate = useCallback(
    (e?: React.MouseEvent) => {
      e?.stopPropagation?.();
      const hasCustomNotification = nodes.some(
        (n) => n.type === "customNotification",
      );
      if (hasCustomNotification) {
        toast.error(
          "Already added Custom Notification. Remove the Custom Notification node and then add Template.",
        );
        return;
      }
      templateCountRef.current += 1;
      const id = `template-${templateCountRef.current}`;
      setNodes((nds) => {
        const withoutTemplate = nds.filter((n) => n.type !== "template");
        const newNode: Node = {
          id,
          type: "template",
          position: { x: NOTIFICATION_X, y: NODE_TOP_Y },
          data: { templateId: "", templateName: "" },
          targetPosition: Position.Left,
        };
        return [...withoutTemplate, newNode];
      });
      setTimeout(
        () => reactFlowInstanceRef.current?.fitView?.({ padding: 0.2 }),
        100,
      );
    },
    [setNodes, nodes],
  );

  const onNodesChange: OnNodesChange = useCallback(
    (changes) => setNodes((nds) => applyNodeChanges(changes, nds)),
    [setNodes],
  );
  const onEdgesChange: OnEdgesChange = useCallback(
    (changes) => setEdges((eds) => applyEdgeChanges(changes, eds)),
    [setEdges],
  );

  const onConnect: OnConnect = useCallback(
    (connection: Connection) => {
      setEdges((eds) => addEdge(connection, eds));
    },
    [setEdges],
  );

  const isValidConnection = useCallback(
    (edgeOrConnection: Connection | Edge) => {
      const sourceId = edgeOrConnection.source;
      const targetId = edgeOrConnection.target;
      if (sourceId !== "segment-1") return false;
      const targetNode = nodes.find((n) => n.id === targetId);
      if (!targetNode) return false;
      return (
        targetNode.type === "customNotification" ||
        targetNode.type === "template"
      );
    },
    [nodes],
  );

  const flowData = useMemo((): FlowBuilderData | null => {
    const segmentNode = nodes.find((n) => n.type === "segment");
    const segmentId = (segmentNode?.data as any)?.segmentId ?? "";
    if (!segmentId) return null;

    const segmentEdge = edges.find((e) => e.source === "segment-1");
    if (!segmentEdge) return null;
    const targetNode = nodes.find((n) => n.id === segmentEdge.target);
    if (!targetNode) return null;

    if (targetNode.type === "customNotification") {
      const d = targetNode.data as any;
      return {
        segmentId,
        notificationType: "custom",
        title: d?.title ?? "",
        body: d?.body ?? "",
        image: d?.image?.trim() || undefined,
        link: d?.link?.trim() || undefined,
        buttons: Array.isArray(d?.buttons) && d.buttons.length > 0
          ? d.buttons.filter(
              (b: any) => b && typeof b.id === "string" && typeof b.text === "string" && typeof b.url === "string",
            )
          : undefined,
      };
    }
    if (targetNode.type === "template") {
      const templateId = (targetNode.data as any)?.templateId ?? "";
      return { segmentId, notificationType: "template", templateId };
    }
    return null;
  }, [nodes, edges]);

  useEffect(() => {
    onFlowDataChange?.(flowData);
  }, [flowData, onFlowDataChange]);

  // Sync segments/templates into node data (replaces context)
  useEffect(() => {
    setNodes((nds) =>
      nds.map((n) => {
        if (n.type === "segment") {
          return { ...n, data: { ...(n.data || {}), segments, templates } };
        }
        if (n.type === "template") {
          return { ...n, data: { ...(n.data || {}), templates } };
        }
        return n;
      }),
    );
  }, [segments, templates, setNodes]);

  const handleWheelCapture = (e: React.WheelEvent) => {
    e.preventDefault();
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();
  };

  return (
    <div
        style={{
          width: "100%",
          minHeight: "calc(100vh - 220px)",
          height: "calc(100vh - 220px)",
          border: "1px solid #e2e8f0",
          borderRadius: 8,
        }}
        onWheelCapture={handleWheelCapture}
      >
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onInit={onInit}
          isValidConnection={isValidConnection}
          nodeTypes={nodeTypes}
          fitView
          fitViewOptions={{ padding: 0.2 }}
          minZoom={0.5}
          maxZoom={1.5}
          zoomOnScroll={false}
          zoomOnPinch={false}
          panOnScroll={false}
          proOptions={{ hideAttribution: true }}
        >
          <Background variant={BackgroundVariant.Dots} gap={12} size={1} />
          <Controls showInteractive={false} />
          <Panel
            position="top-right"
            className="nodrag"
            style={{ marginTop: 8, marginRight: 8, display: "flex", gap: 8 }}
          >
            <button
              type="button"
              onClick={(e) => handleAddCustomNotification(e)}
              style={{
                padding: "6px 12px",
                fontSize: 13,
                fontWeight: 500,
                border: "1px solid #86efac",
                borderRadius: 6,
                background: "#f0fdf4",
                color: "#166534",
                cursor: "pointer",
              }}
            >
              Add Custom Notification
            </button>
            <button
              type="button"
              onClick={(e) => handleAddTemplate(e)}
              style={{
                padding: "6px 12px",
                fontSize: 13,
                fontWeight: 500,
                border: "1px solid #93c5fd",
                borderRadius: 6,
                background: "#eff6ff",
                color: "#1e40af",
                cursor: "pointer",
              }}
            >
              Add Template
            </button>
          </Panel>
        </ReactFlow>
      </div>
  );
}

export default function FlowBuilderCanvas(props: FlowBuilderCanvasProps) {
  return (
    <ReactFlowProvider>
      <FlowBuilderCanvasInner
        segments={props.segments}
        templates={props.templates}
        onFlowDataChange={props.onFlowDataChange}
        initialFlowData={props.initialFlowData}
      />
    </ReactFlowProvider>
  );
}
